import type { RequestOptions } from 'graphql-request';

import type { LocationInventoryDTO } from '@invenco/common-interface/inventory';

import { DEFAULT_PAGE_SIZE } from '@/constants';
import {
  type InventoriesQueryQuery,
  type InventoriesQueryQueryVariables,
  type InventoriesBoolExp,
  type InventoriesOrderByExp,
  OrderBy,
} from '@/graphql/generated/seller/graphql';

import { BaseGateway, type QueryRequestOptions } from '../BaseGateway';
import type {
  GatewayArrayFilter,
  GatewayBooleanFilter,
  GatewayFilter,
  GatewayName,
  GatewayNumberFilter,
  GatewayQueryProps,
  PaginatedResponse,
} from '../types';
import { inventoriesQuery } from './gql/seller/inventory';

function getFilterWhereClause(
  fieldName: keyof Omit<InventoriesBoolExp, '_and' | '_not' | '_or'>,
  filter: GatewayFilter,
): InventoriesBoolExp {
  switch (filter.operator) {
    case 'eq':
      return { [fieldName]: { _eq: filter.value } };
    case 'ne':
      return { [fieldName]: { _neq: filter.value } };
    case 'like':
      return { [fieldName]: { _like: `%${filter.value}%` } };
    case 'notLike':
      return { [fieldName]: { _nlike: `%${filter.value}%` } };
    case 'in':
      return { [fieldName]: { _in: filter.value } };
    case 'notIn':
      return { [fieldName]: { _nin: filter.value } };
    case 'lt':
      return { [fieldName]: { _lt: filter.value } };
    case 'lte':
      return { [fieldName]: { _lte: filter.value } };
    case 'gt':
      return { [fieldName]: { _gt: filter.value } };
    case 'gte':
      return { [fieldName]: { _gte: filter.value } };
    default:
      return {};
  }
}

export type SortField =
  | 'id'
  | 'skuName'
  | 'locationName'
  | 'qtyExpected'
  | 'qtyOnHand'
  | 'qtyReserved'
  | 'qtyAvailable';

export type QueryInventoriesProps = GatewayQueryProps<{
  sortKey: SortField;
  filters: {
    skuName: GatewayArrayFilter;
    locationName: GatewayArrayFilter;
    qtyExpected: GatewayNumberFilter;
    qtyOnHand: GatewayNumberFilter;
    qtyReserved: GatewayNumberFilter;
    qtyAvailable: GatewayNumberFilter;
    isSkuArchived: GatewayBooleanFilter;
  };
}>;

export class InventoryGateway extends BaseGateway {
  name: GatewayName = 'inventory';

  async getInventories(
    query?: {
      take?: number;
      cursor?: string;
      search?: string;
      locationIds?: string[];
      skuName?: string;
      skuIds?: string[];
    },
    options?: QueryRequestOptions,
  ): Promise<PaginatedResponse<LocationInventoryDTO>> {
    const params = {
      take: query?.take,
      cursor: query?.cursor,
      q: query?.search,
      locationIds: query?.locationIds?.join(','),
      skuName: query?.skuName,
      skuIds: query?.skuIds?.join(','),
    };
    const { data } = await this.httpGet('/v1/inventories', { params, ...options });
    return { ...data, items: data.inventories };
  }

  async queryInventories(
    input: QueryInventoriesProps,
    options: { signal?: RequestOptions['signal'] },
  ): Promise<InventoriesQueryQuery> {
    // Ensure limit is always a positive number
    const limit = Math.max(1, Math.abs(input.take ?? DEFAULT_PAGE_SIZE));

    const whereClause: InventoriesBoolExp[] = [];

    if (input.search) {
      const searchPattern = `%${input.search}%`;
      whereClause.push({
        _or: [
          { skuName: { _ilike: searchPattern } },
          { skuDescription: { _ilike: searchPattern } },
        ],
      });
    }

    const { filters } = input;

    if (filters?.skuName && filters.skuName.value.length > 0) {
      whereClause.push(getFilterWhereClause('skuName', filters.skuName));
    }
    if (filters?.locationName && filters.locationName.value.length > 0) {
      whereClause.push(getFilterWhereClause('locationName', filters.locationName));
    }
    if (filters?.qtyExpected) {
      whereClause.push(getFilterWhereClause('qtyExpected', filters.qtyExpected));
    }
    if (filters?.qtyOnHand) {
      whereClause.push(getFilterWhereClause('qtyOnHand', filters.qtyOnHand));
    }
    if (filters?.qtyReserved) {
      whereClause.push(getFilterWhereClause('qtyReserved', filters.qtyReserved));
    }
    if (filters?.qtyAvailable) {
      whereClause.push(getFilterWhereClause('qtyAvailable', filters.qtyAvailable));
    }
    if (filters?.isSkuArchived) {
      whereClause.push({
        sku: {
          archivedAt: { _is_null: !filters.isSkuArchived.value },
        },
      });
    }

    /**
     * Sorting
     */
    const orderBy = input.sortBy
      ? input.sortBy.map(({ key, direction }) => {
          const orderByDirection = direction === 'desc' ? OrderBy.Desc : OrderBy.Asc;
          const orderByClause: InventoriesOrderByExp = {};
          orderByClause[key] = orderByDirection;
          return orderByClause;
        })
      : [];

    const variables: InventoriesQueryQueryVariables = {
      limit,
      offset: input.offset ?? 0,
      where: whereClause.length > 0 ? { _and: whereClause } : {},
      orderBy,
    };

    return this.graphqlQuery<InventoriesQueryQuery, InventoriesQueryQueryVariables>(
      inventoriesQuery,
      variables,
      options.signal,
    );
  }
}
